import { Alert, Box, Skeleton, Stack } from "@mui/material";
import { ProductionTable } from "components/plan/table/production/ProductionTable";
import { useTenantTranslation } from "hooks/formatters";
import { Period } from "hooks/periodIndex";
import { useTenant } from "hooks/settings";
import { usePeriodNameFormatter } from "hooks/usePeriodNameFormatter";
import React from "react";
import {
  ChefGroup,
  MaterialRead,
  PeriodSummary,
  ProductGroup,
  useGetPlanQuery,
  useGetPlanResultsQuery,
} from "src/store/api/generatedApi";
import { deployPlanFormSlice } from "src/store/slices/deployPlanFormSlice";
import { useAppDispatch } from "src/store/store";
import { PlanId } from "components/common/boundary/PlanId";

interface Props {
  preview: {
    planId: PlanId;
    period: Period;
    title: string;
    isBackup: boolean;
  } | null;
  periods: PeriodSummary[];
  planId: PlanId;
  productGroups: ProductGroup[] | null;
  chefGroups: ChefGroup[];
  materials: MaterialRead[];
}

interface PreviewTableProps {
  planId: PlanId;
  period: Period;
  productGroups: ProductGroup[] | null;
  chefGroups: ChefGroup[];
  materials: MaterialRead[];
}

const PreviewTable = ({
  planId,
  period,
  productGroups,
  chefGroups,
  materials,
}: PreviewTableProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { currentData: plan } = useGetPlanQuery({
    tenantName,
    planId,
  });
  const { currentData: planResults } = useGetPlanResultsQuery({
    tenantName,
    planId,
    period,
  });
  const periodResults = planResults?.periods[period.toString()];
  if (plan && planResults) {
    if (!periodResults || periodResults.mixes.length === 0) {
      return <Alert severity="info">{t("noMixesFound")}</Alert>;
    }
    return (
      <ProductionTable
        key={`${plan.id}.${period}`}
        sx={{ width: "100%" }}
        period={period}
        productGroups={productGroups}
        chemistryGroups={chefGroups}
        mixResults={periodResults.mixes}
        productGroupResults={periodResults.product_group_results}
        mixSummaries={plan.summary.mix_summary}
        periodMaterialSummaries={planResults.summary.period_material_summary}
        materials={materials}
        mixMetadata={plan.mix_metadata}
      />
    );
  } else {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "150px repeat(8, 80px)",
          gridTemplateRows: "35px 90px repeat(10, 35px)",
          gap: 0.5,
        }}
      >
        <Skeleton variant="rectangular" sx={{ gridColumn: "1/3" }} />
        <Skeleton variant="rectangular" sx={{ gridColumn: "6/8" }} />
        <Skeleton variant="rectangular" sx={{ gridColumn: "8/10" }} />
        {new Array(99).fill(null).map((_, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <Skeleton key={index} variant="rectangular" sx={{ height: "100%" }} />
        ))}
      </Box>
    );
  }
};

export const DeployForm = ({
  preview,
  periods,
  planId,
  materials,
  chefGroups,
  productGroups,
}: Props) => {
  const periodNameFormatter = usePeriodNameFormatter();
  const dispatch = useAppDispatch();

  React.useLayoutEffect(() => {
    const [period] = periods;
    if (!preview && period) {
      dispatch(
        deployPlanFormSlice.actions.addPeriod({
          planId,
          period: period.period as Period,
        })
      );
      dispatch(
        deployPlanFormSlice.actions.setPreview([
          planId,
          {
            planId,
            period: period.period as Period,
            title: periodNameFormatter(period.period as Period, {
              startTimestamp: period.start_timestamp,
              endTimestamp: period.end_timestamp,
              name: period.name,
            }),
            isBackup: false,
          },
        ])
      );
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- periodNameFormatter is a hook-returned formatter with potentially unstable identity; effect should only re-run when periods/preview change
  }, [periods, preview, dispatch, planId]);

  return (
    <Stack>
      <Stack>
        {preview ? (
          <PreviewTable
            planId={preview.planId}
            period={preview.period}
            productGroups={productGroups}
            chefGroups={chefGroups}
            materials={materials}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};
